import { Observable } from "core/Misc/observable";
import { GetClass, RegisterClass } from "core/Misc/typeStore";
import { Quaternion, Vector3 } from "core/Maths/math.vector";
import { RewardGraphNode } from "../rewardGraphNode";
import { RewardGraphConnectionPointTypes } from "../rewardGraphConnectionPointTypes";
import { RewardGraphContextualSources } from "../rewardGraphContextualSources";
import { RewardGraphConnectionPoint } from "../rewardGraphConnectionPoint";
import { RewardGraphBuildState } from "../rewardGraphBuildState";
import { Nullable } from "core/types";

export enum InputSpecializedDisplayType {
    Generic,
    GroundTouch,
    TipAngle,
    HeightCap
}

export function InputSpecializedDisplayTypeString(operation : InputSpecializedDisplayType) : string {
    if( typeof operation === 'number' )
        return InputSpecializedDisplayType[operation];
    return operation as string;
}

export function StringInputSpecializedDisplay(op : string) {
    return (InputSpecializedDisplayType as any)[op];
}

/**
 * Block used to expose an input value
 */
export class RewardInputBlock extends RewardGraphNode {
    private _storedValue: any;
    private _valueCallback: () => any;
    private _type: RewardGraphConnectionPointTypes = RewardGraphConnectionPointTypes.Undefined;
    private _contextualSource = RewardGraphContextualSources.None;
    private _isConstant = false;

    /** @internal */
    public _enablingBlock: Nullable<RewardInputBlock> = null;
    /** @internal */
    public _tempEnablingBlockUniqueId: number = -1;

    public displayType : InputSpecializedDisplayType = InputSpecializedDisplayType.Generic;

    public label: string = '';

    /** Gets or set a value used to limit the range of float values */
    public min: number = 0;

    public minValLabel: string = '';

    /** Gets or set a value used to limit the range of float values */
    public max: number = 0;

    public maxValLabel: string = '';

    public displaySort: number = 0;

    /** Gets or sets the group to use to display this block in the Inspector */
    public groupInInspector = "";

    /** Gets an observable raised when the value is changed */
    public onValueChangedObservable = new Observable<RewardInputBlock>();

    /**
     * Gets or sets the connection point type (default is float)
     */
    public get type(): RewardGraphConnectionPointTypes {
        if (this._type === RewardGraphConnectionPointTypes.AutoDetect) {
            if (this.value != null) {
                if (!isNaN(this.value)) {
                    this._type = RewardGraphConnectionPointTypes.Float;
                    return this._type;
                }

                switch (this.value.getClassName()) {
                    case "Vector3":
                        this._type = RewardGraphConnectionPointTypes.Vector3;
                        return this._type;
                    case "Quat":
                        this._type = RewardGraphConnectionPointTypes.Quat;
                        return this._type;
                    case "PartID":
                        this._type = RewardGraphConnectionPointTypes.PartID;
                        return this._type;
                    case "Boolean":
                        this._type = RewardGraphConnectionPointTypes.Boolean;
                        return this._type;
                }
            }
        }

        return this._type;
    }

    /**
     * Gets a boolean indicating that the current connection point is a contextual value
     */
    public get isContextual(): boolean {
        return this._contextualSource !== RewardGraphContextualSources.None;
    }

    public get isConstant(): boolean {
        return this._isConstant;
    }

    public set isConstant(v:boolean) {
        this._isConstant = v;
    }
    /**
     * Gets or sets the current contextual value
     */
    public get contextualValue(): RewardGraphContextualSources {
        return this._contextualSource;
    }

    public set contextualValue(value: RewardGraphContextualSources) {
        this._contextualSource = value;

        if (this.output) {
            this.output.type = this._type;
        }
    }

    /**
     * Creates a new InputBlock
     * @param name defines the block name
     * @param type defines the type of the input (can be set to RewardGraphConnectionPointTypes.AutoDetect)
     */
    public constructor(name: string, type: RewardGraphConnectionPointTypes = RewardGraphConnectionPointTypes.AutoDetect) {
        super(name);

        this._type = type;
        this._isInput = true;

        this.setDefaultValue();

        this.registerOutput("output", type);
    }

    /**
     * Gets or sets the value of that point.
     * Please note that this value will be ignored if valueCallback is defined
     */
    public get value(): any {
        return this._storedValue;
    }

    public set value(value: any) {
        if (this.type === RewardGraphConnectionPointTypes.Float) {
            if (this.min !== this.max) {
                value = Math.max(this.min, value);
                value = Math.min(this.max, value);
            }
        }

        this._storedValue = value;
        this.onValueChangedObservable.notifyObservers(this);
    }

    /**
     * Gets or sets a callback used to get the value of that point.
     * Please note that setting this value will force the connection point to ignore the value property
     */
    public get valueCallback(): () => any {
        return this._valueCallback;
    }

    public set valueCallback(value: () => any) {
        this._valueCallback = value;
    }

    /**
     * Gets the current class name
     * @returns the class name
     */
    public override getClassName() {
        return "RewardInputBlock";
    }

    /**
     * Gets the reward output component
     */
    public get output(): RewardGraphConnectionPoint {
        return this._outputs[0];
    }

    /**
     * Set the input block to its default value (based on its type)
     */
    public setDefaultValue() {
        this.contextualValue = RewardGraphContextualSources.None;
        switch (this.type) {
            case RewardGraphConnectionPointTypes.Float:
                this.value = 0;
                break;
            case RewardGraphConnectionPointTypes.Vector3:
                this.value = Vector3.Zero();
                break;
            case RewardGraphConnectionPointTypes.Quat:
                this.value = Quaternion.Identity();
                break;
            case RewardGraphConnectionPointTypes.PartID:
                this.value = '';
                break;
            case RewardGraphConnectionPointTypes.Boolean:
                this.value = false;
                break;
        }
    }

    protected override _buildBlock(state: RewardGraphBuildState) {
        super._buildBlock(state);

        if (this.isContextual) {
            this.output._storedValue = null;
            this.output._storedFunction = (state) => {
                return state.getContextualValue(this._contextualSource);
            };
        } else {
            switch (this._type) {
                case  RewardGraphConnectionPointTypes.PartID: {
                    if( typeof this.value === 'string' ) {
                        const func : (state: RewardGraphBuildState)=>any = (state) => {
                            if( state.queryContext )
                                return state.queryContext.getPartIndex(this.value);
                            return 0;
                        };
                        this.output._storedFunction = (state) => {
                            return func(state);
                        }
                    } else {
                        this.output._storedFunction = null;
                        this.output._storedValue = this.value;
                    }
                    break;
                }
                default:
                    this.output._storedFunction = null;
                    this.output._storedValue = this.value;
            }
        }
    }

    public override dispose() {
        this.onValueChangedObservable.clear();

        super.dispose();
    }

    protected override _dumpPropertiesCode() {
        const variableName = this._codeVariableName;

        if (this.isContextual) {
            return (
                super._dumpPropertiesCode() + `${variableName}.contextualValue = MELS.RewardInputBlock.${RewardGraphContextualSources[this._contextualSource]};\n`
            );
        }
        const codes: string[] = [];

        let valueString = "";

        switch (this.type) {
            case RewardGraphConnectionPointTypes.Boolean:
                valueString = `${this.value.toString()}`;
                break;
            case RewardGraphConnectionPointTypes.Float:
            case RewardGraphConnectionPointTypes.PartID:
                valueString = `${this.value}`;
                break;
            case RewardGraphConnectionPointTypes.Vector3:
                valueString = `new BABYLON.Vector3(${this.value.x}, ${this.value.y}, ${this.value.z})`;
                break;
            case RewardGraphConnectionPointTypes.Quat:
                valueString = `new BABYLON.Quaternion(${this.value.x}, ${this.value.y}, ${this.value.z}, ${this.value.w})`;
                break;
        }

        // Common Property "Value"
        codes.push(`${variableName}.value = ${valueString}`);

        // Float-Value-Specific Properties
        if (this.type === RewardGraphConnectionPointTypes.Float ) {
            codes.push(`${variableName}.min = ${this.min}`, `${variableName}.max = ${this.max}`);
        }

        codes.push("");

        return super._dumpPropertiesCode() + codes.join(";\n");
    }

    public override serialize(): any {
        const serializationObject = super.serialize();

        serializationObject.type = this.type;
        serializationObject.isConstant = this.isConstant;
        serializationObject.contextualValue = this.contextualValue;
        serializationObject.label = this.label;
        serializationObject.min = this.min;
        serializationObject.minValLabel = this.minValLabel;
        serializationObject.max = this.max;
        serializationObject.maxValLabel = this.maxValLabel;
        serializationObject.displaySort = this.displaySort;
        serializationObject.groupInInspector = this.groupInInspector;
        serializationObject.displayType = InputSpecializedDisplayTypeString(this.displayType);
        serializationObject.enablingBlock = this._enablingBlock?.uniqueId ?? -1;

        if (this._storedValue !== null && !this.isContextual) {
            if (this._storedValue.asArray) {
                serializationObject.valueType = "BABYLON." + this._storedValue.getClassName();
                serializationObject.value = this._storedValue.asArray();
            } else if ( this.type  === RewardGraphConnectionPointTypes.Boolean ) {
                serializationObject.valueType = "boolean";
                serializationObject.value = this._storedValue;
            } else if (this._type === RewardGraphConnectionPointTypes.PartID) {
                serializationObject.valueType = "partID";
                serializationObject.value = (this._storedValue && typeof this._storedValue === 'string') ? this._storedValue : 'Torso';
            } else {
                serializationObject.valueType = "number";
                serializationObject.value = this._storedValue;
            }
        }

        return serializationObject;
    }

    public override _deserialize(serializationObject: any) {
        super._deserialize(serializationObject);

        this._type = serializationObject.type;
        this._isConstant = serializationObject.isConstant;

        this.contextualValue = serializationObject.contextualValue;
        this.label = serializationObject.label || '';
        this.min = serializationObject.min || 0;
        this.minValLabel = serializationObject.minValLabel || '';
        this.max = serializationObject.max || 0;
        this.maxValLabel = serializationObject.maxValLabel || '';
        this.displaySort = serializationObject.displaySort || 0;
        this.groupInInspector = serializationObject.groupInInspector || "";
        this.displayType = serializationObject.displayType ? StringInputSpecializedDisplay(serializationObject.displayType) : InputSpecializedDisplayType.Generic;
        this._tempEnablingBlockUniqueId = serializationObject.enablingBlock || -1;

        if (!serializationObject.valueType) {
            return;
        }

        if (serializationObject.valueType === "number") {
            this._storedValue = serializationObject.value;
        } else if( this._type === RewardGraphConnectionPointTypes.Boolean ) {
            this._storedValue = serializationObject.value;
        } else if( this._type === RewardGraphConnectionPointTypes.PartID ) {
            this._storedValue = serializationObject.value;
        } else {
            const valueType = GetClass(serializationObject.valueType);

            if (valueType) {
                this._storedValue = valueType.FromArray(serializationObject.value);
            }
        }
    }
}

RegisterClass("MELS.RewardInputBlock", RewardInputBlock);
